/* global __PREFIX_PATHS__, __PATH_PREFIX__ */
import React from 'react'
import { withRouter } from 'react-router'
import Helmet from 'react-helmet'

//
// 国際化対応をするための処理を提供します。
//
// ### 使い方
//
// `i18n()` 関数のドキュメントを参考にしてください。
//
//
// ### 国際化対応の実装方法
//
// Gatsby 自身は国際化機能を提供していないので、自前で用意しています。
// 手軽なものがなかったので国際化のライブラリも使っていません。
//
// 判別方法は「`/en` で始まるパスは英語サイト、それ以外は日本語サイト」というルールにしています。
// Gatsby は React Router を使っているため、パスの取得には React Router の `withRouter`
// などを使って location を取得する必要があります。
//
// なお `i18n()` などを使う場合は内部で `withRouter` を使っているので呼び出し側で
// import する必要はありません。
//

/**
 * プレフィックス(/2018/など)付きの場合、そのパスのパターンの文字列になります。
 * それ以外の場合は空文字になります。
 *
 * ### プレフィックス付きの時の挙動について
 *
 * 現状、プレフィックス付きの場合でも空文字にもマッチするようにしています。
 * これはビルド時にプレフィックスがつかない `location` が渡される場合があるためです。
 *
 * 例: `/2018` をプレフィックスにした時に `withRouter` を使った時の `location` で以下が起こります。
 * - ビルド時には `pathname` にプレフィックスが入らない(`/en/` など)
 * - ブラウザ実行時には `pathname` にプレフィックスが入る(`/2018/en/` など)
 *
 * この挙動は Gatsby の仕様かバグなのか判断つかないので、両方のパターンを許容しています。
 */
// const prefixPath = __PREFIX_PATHS__ ? __PATH_PREFIX__ : ''
const prefixPath = __PREFIX_PATHS__ ? `(?:${__PATH_PREFIX__})?` : ''

/**
 * サイトの言語指定部分の正規表現です。
 * 
 * 例えば以下のような文字列にマッチします。
 *
 * - `/` … 日本語ページ(常にマッチ)
 * - `/en/` … 英語ページ(常にマッチ)
 * - `/2018/` … 日本語ページ(プレフィックスとして /2018 が指定された場合)
 * - `/2018/en/` … 英語ページ(プレフィックスとして /2018 が指定された場合)
 */
const prefix = new RegExp(`^${prefixPath}/(?:en/|en$)?`)

/**
 * 英語サイトの言語指定部分のパターンです。
 * 
 * 例えば以下のような文字列にマッチします。
 *
 * - `/en/` … 英語ページ(常にマッチ)
 * - `/2018/en/` … 英語ページ(プレフィックスとして /2018 が指定された場合)
 */
const enPrefix = new RegExp(`^${prefixPath}/(?:en/|en$)`)

// ----------------------------------------------------------------
//     高レベル API
// ----------------------------------------------------------------

/**
 * 国際化テキストを提供するコンポーネントを作成します。
 * 
 * 国際化テキストには、通常のテキストの他に、React の Element を格納することもできます。
 *
 * @param {Object} texts テキストの定義
 * @param {Object} texts.en 英語のテキスト定義
 * @param {Object} texts.ja 日本語のテキスト定義
 *
 * @example
 * const Text = i18n({
 *   en: {
 *     title: `English Page`,
 *     ...
 *   },
 *   ja: {
 *     title: `日本語ページ`,
 *     ...
 *   }
 * });
 *
 * export default function index() {
 *   return (<h1><Text id="title" /></h1>)
 * }
 * 
 */
export default function i18n (texts) {
  return withRouter(function ({ id, location, nl2br }) {
    const ln = lang(location)
    const local = texts[ln]
    if (!local) throw new Error(`unexpected lang: ${ln}`)

    const t = resolvePropByKeyString(local, id)
    if (!t) throw new Error(`text not found: ${id}, lang=${ln}`)

    return (typeof t === 'string') ? textToElement(t, nl2br) : t
  })
}

/**
 * 与えられたテキストを Element に変換します。
 * 
 * @param {string} text 変換対象のテキスト。
 * @param {boolean} nl2br true の場合は改行に対して <br> を挿入します。
 * @returns {React.Component}
 */
export function textToElement (text, nl2br) {
  if (nl2br) {
    // 参考: http://qiita.com/kouheiszk/items/e7c74ab5eab901f89a7f#comment-1103a64734b3ba09dc51
    const nl2brFunc = (str) => {
      const regex = /([\n\r])/g
      return str.split(regex).map(line => {
        return line.match(regex) ? <br /> : line
      })
    }
    return (<span>{nl2brFunc(text)}</span>)
  }
  return (<span>{text}</span>)
}

/**
 * オブジェクトからkey文字列のプロパティを取り出します。
 * ピリオド記号でネストしたプロパティを取り出せます。
 * 
 * 例: resolvePropByKeyString({a:{b:{c:123}}}, 'a.b.c') → 123
 * 
 * @param {Object} obj 
 * @param {string} key 
 */
export function resolvePropByKeyString (obj, key) {
  const keys = key.split('.')
  let tmp = obj
  for (const k of keys) {
    if (!tmp) { return null }
    tmp = tmp[k]
  }
  return tmp
}

/**
 * ページ個別にtitleとdescriptionを設定するコンポーネント。
 * layout/index.jsで設定しているデフォルトのtitle,descriptionを上書きします。
 */
export const TitleDescription = withRouter(({ texts, location }) => {
  const ln = lang(location)
  const desc = texts[ln].desc
  const title = texts[ln].title + ' | DroidKaigi 2018'
  const meta = [{ property: 'og:title', content: title }]
  if (desc) {
    meta.push(
      { name: 'description', content: desc },
      { property: 'og:description', content: desc }
    )
  }
  return <Helmet title={title} meta={meta} />
})

// ----------------------------------------------------------------
//     低レベル API
// ----------------------------------------------------------------

/**
 * 現在のページの言語を取得します。
 *
 * @param {Object} location 
 */
export function lang (location) {
  return location.pathname.match(enPrefix) ? 'en' : 'ja'
}

/**
 * 指定のパスに対して現在の言語設定のプレフィックスを追加します。
 *
 * @param {Object} location 
 * @param {string} path 
 */
export function resolve (location, path) {
  if (!path.match(/^\//)) throw Error(`path must begin with '/': ${path}`)
  return lang(location) === 'en' ? `/en${path}` : path
}

/**
 * 現在の言語設定のプレフィックスを取り除いたパスを返します。
 *
 * @param {Object} location 
 * @returns {string}
 */
export function pagePath (location) {
  return location.pathname.replace(prefix, '/')
}

/**
 * 現在の環境に応じて /2018 というプレフィックスを付与します
 *
 * @param {string} path
 */
export function withPrefix (path) {
  if (__PREFIX_PATHS__) {
    return `${__PATH_PREFIX__}${path}`
  }
  return path
}



// WEBPACK FOOTER //
// ./src/utils/i18n.js